import { handleResponseOld, makeGet } from './helpers';
import { BiddingInfo } from '@/types/BiddingInfo';

/**
 * @category Item API
 * @see fetchBidderBiddingAll
 */
type FetchBidderBiddingAllResponse = {
    data: {
        biddingInfos: BiddingInfo[];
    };
    message: string;
    success: boolean;
};

/**
 * @category Item API
 * @see fetchBidderBiddingAll
 */
type FetchBidderBiddingAllParams = {
    authToken: string;
    deployment: string;
};

/**
 * GET request to item-api service to retrieve all bidding information for authed user
 * @function fetchBidderBiddingAll
 * @category Item API
 * @param {FetchBidderBiddingAllParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/bidder/bidding/all
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/bidding/routes/get-bidder-bidding-info-all.ts
 */
export const fetchBidderBiddingAll = ({ authToken, deployment }: FetchBidderBiddingAllParams) =>
    new Promise<FetchBidderBiddingAllResponse>((resolve, reject) => {
        const request = makeGet({ authToken, deployment, path: '<ITEM-API>bidder/bidding/all' });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Item API
 * @see fetchBidderBiddingUpcoming
 */
type FetchBidderBiddingUpcomingParams = {
    authToken: string;
    deployment: string;
};

/**
 * @category Item API
 * @see fetchBidderBiddingUpcoming
 */
type FetchBidderBiddingUpcomingResponse = {
    data: {
        biddingInfos: BiddingInfo[];
    };
    message: string;
    success: boolean;
};

/**
 * GET request to item-api service to retrieve upcoming bidding information for authed user
 * @function fetchBidderBiddingUpcoming
 * @category Item API
 * @param {FetchBidderBiddingUpcomingParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/bidder/bidding/upcoming
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/bidding/routes/get-bidder-bidding-info-upcoming.ts
 */
export const fetchBidderBiddingUpcoming = ({ authToken, deployment }: FetchBidderBiddingUpcomingParams) =>
    new Promise<FetchBidderBiddingUpcomingResponse>((resolve, reject) => {
        const request = makeGet({ authToken, deployment, path: '<ITEM-API>bidder/bidding/upcoming' });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Item API
 * @see fetchCatalogBidding
 */
type FetchCatalogBiddingResponse = {
    data: [
        {
            bidActivityCount: number;
            bidCount: number;
            isAvailable: boolean;
            isLocked: boolean;
            isPassed: boolean;
            isReserveMet: boolean;
            isSold: boolean;
            itemId: number;
            leadingBid: number;
            salePrice: number;
        }
    ];
    message: string;
    success: boolean;
};

/**
 * @category Item API
 * @see fetchCatalogBidding
 */
type FetchCatalogBiddingParams = {
    authToken: string;
    catalogId: number;
    deployment: string;
};

/**
 * GET request to item-api service to retrieve catalog-level bidding information for authed user
 * @function fetchCatalogBidding
 * @category Item API
 * @param {FetchCatalogBiddingParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/spa/small/catalog/${catalogId}/bidding
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/bidding/routes/get-catalog-bidding-info-old.ts
 */
export const fetchCatalogBidding = ({ authToken, catalogId, deployment }: FetchCatalogBiddingParams) =>
    new Promise<FetchCatalogBiddingResponse>((resolve, reject) => {
        const request = makeGet({
            authToken,
            deployment,
            path: `<ITEM-API>spa/small/catalog/${catalogId}/bidding`,
        });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Item API
 * @see fetchBidderBiddingUpcoming
 */
type FetchItemBiddingResponse = {
    data: BiddingInfo[];
    message: string;
    success: boolean;
};

/**
 * @category Item API
 * @see fetchItemBidding
 */
type FetchItemBiddingParams = {
    authToken: string;
    deployment: string;
    itemId: number;
};

/**
 * GET request to item-api service to fetch single item bidding info for authed user
 * @function fetchItemBidding
 * @category Item API
 * @param {FetchItemBiddingParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/spa/small/item/${itemId}/bidding
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/bidding/routes/get-item-bidding-info-old.ts
 */
export const fetchItemBidding = ({ authToken, deployment, itemId }: FetchItemBiddingParams) =>
    new Promise<FetchItemBiddingResponse>((resolve, reject) => {
        const request = makeGet({ authToken, deployment, path: `<ITEM-API>spa/small/item/${itemId}/bidding` });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });
